import { useCallback } from 'react';

import { Patient, PatientSimple } from '@dreryk/egabinet-patient-frontend-api';

import { getCurrentLocale, useFormController } from '@dreryk/react-common';
import { FormRouteLeavingPrompt, StyledFlex } from '@dreryk/react-components';

import { selectInitialPersonalData } from '../common/common.utils';
import { ContactDetailsFormSection } from '../common/ContactDetailsFormSection';
import { PersonalDataFormSection } from '../common/PersonalDataFormSection';
import { IdentityDocumentFormSection } from '../identityDocument/IdentityDocumentFormSection';

import { isPatientDataConsistent } from './comparedPatientPreview/comparedPatientPreview.utils';
import { ComparedPatientPreviewContactDetailsSection } from './comparedPatientPreview/ComparedPatientPreviewContactDetailsSection';
import { ComparedPatientPreviewPersonalDataSection } from './comparedPatientPreview/ComparedPatientPreviewPersonalDataSection';
import { PatientUnconfirmedFormInconsistentDataStatus } from './PatientUnconfirmedFormInconsistentDataStatus';
import { PatientUnconfirmedFormBean, PatientUnconfirmedFormController } from './patientUnconfirmedUpdate.types';
import { StyledPatientUnconfirmedForm } from './StyledPatientUnconfirmedForm';

type PatientUnconfirmedFormChildrenRenderProps = {
   handleSubmit: PatientUnconfirmedFormController['handleSubmit'];
   formState: PatientUnconfirmedFormController['formState'];
   reset: PatientUnconfirmedFormController['reset'];
   trigger: PatientUnconfirmedFormController['trigger'];
};
type PatientUnconfirmedFormProps = {
   readonly patient: Patient;
   readonly comparedPatient?: Omit<PatientSimple, 'medicalDocumentationCreated'>;
   readonly children: (renderProps: PatientUnconfirmedFormChildrenRenderProps) => JSX.Element;
};

const PatientUnconfirmedForm = (props: PatientUnconfirmedFormProps): JSX.Element => {
   const {
      handleSubmit,
      register,
      setValue,
      formState,
      watch,
      reset,
      trigger,
      setError,
      getValues,
   } = useFormController<PatientUnconfirmedFormBean>(selectInitialPersonalData(props.patient));
   const locale = getCurrentLocale();

   const currentFormValues = watch();

   const handleFieldCopy = useCallback(
      (fieldName: string, value: unknown) => {
         setValue(fieldName, value, {
            shouldDirty: true,
         });
      },
      [setValue]
   );

   return (
      <StyledPatientUnconfirmedForm>
         {props.comparedPatient && !isPatientDataConsistent(currentFormValues, props.comparedPatient, locale) ? (
            <PatientUnconfirmedFormInconsistentDataStatus />
         ) : null}
         <StyledFlex alignItems={'flex-start'} justifyContent={'space-between'} gap={'18px'}>
            <PersonalDataFormSection
               register={register}
               watch={watch}
               setValue={setValue}
               setError={setError}
               trigger={trigger}
               getValues={getValues}
               reset={reset}
               formState={formState}
               patientId={props.patient.id}
            />
            {props.comparedPatient ? (
               <ComparedPatientPreviewPersonalDataSection
                  patient={currentFormValues}
                  comparedPatient={props.comparedPatient}
                  onCopy={handleFieldCopy}
               />
            ) : null}
         </StyledFlex>
         <StyledFlex alignItems={'flex-start'} justifyContent={'space-between'} gap={'18px'}>
            <ContactDetailsFormSection register={register} watch={watch} />
            {props.comparedPatient ? (
               <ComparedPatientPreviewContactDetailsSection
                  patient={currentFormValues}
                  comparedPatient={props.comparedPatient}
                  onCopy={handleFieldCopy}
               />
            ) : null}
         </StyledFlex>
         <IdentityDocumentFormSection name={'identityDocument'} register={register} watch={watch} setValue={setValue} />
         <FormRouteLeavingPrompt formState={formState} />
         {props.children({ handleSubmit: handleSubmit, formState: formState, reset: reset, trigger: trigger })}
      </StyledPatientUnconfirmedForm>
   );
};

export { PatientUnconfirmedForm };
